import axios from 'axios';

import heartIcon from '@img/icons/heart.svg?raw';
import heartFilledIcon from '@img/icons/heart-filled.svg?raw';

const STORAGE_KEY = 'lh_favourites';
const MAX_FAVOURITES = 20;

export function getFavourites() {
    try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY));

        if (!Array.isArray(stored)) {
            return [];
        }

        return stored
            .map((id) => parseInt(id))
            .filter((id) => !isNaN(id));
    } catch (error) {
        return [];
    }
}

function setFavourites(ids) {
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));

        return true;
    } catch (error) {
        // localStorage can throw when full or unavailable (e.g. Safari Private Mode).
        return false;
    }
}

// A shortlist shared with this browser, read from /shortlist/?houses=1,2,3.
//
// Shortlists live in localStorage and have no server-side existence, so the
// URL IS the list - which is what makes one shareable at all without a schema
// or an account. Returns null when the page is showing the viewer's own list,
// so callers can tell "shared, but empty" from "not shared".
//
// Capped and integer-filtered here as well as at the endpoint: this value
// decides what the page renders, and the endpoint's own cap protects the
// server rather than the DOM.
export function sharedFavourites() {
    const param = new URLSearchParams(window.location.search).get('houses');

    if (!param) {
        return null;
    }

    const ids = param
        .split(',')
        .map((id) => parseInt(id, 10))
        .filter((id) => !isNaN(id) && id > 0)
        .slice(0, MAX_FAVOURITES);

    return ids.length ? ids : null;
}

export function isFavourite(id) {
    return getFavourites().includes(parseInt(id));
}

export function favouriteCount() {
    return getFavourites().length;
}

// Toggles a property in the local store and returns whether it is now saved.
// Mirrors the old server cap: silently refuse (and alert) once 20 are stored.
export function toggleFavourite(id) {
    id = parseInt(id);

    const favourites = getFavourites();
    const index = favourites.indexOf(id);

    if (index > -1) {
        favourites.splice(index, 1);
        setFavourites(favourites);

        return false;
    }

    if (favourites.length >= MAX_FAVOURITES) {
        alert(`You can only shortlist ${MAX_FAVOURITES} properties`);

        return false;
    }

    favourites.push(id);
    setFavourites(favourites);

    return true;
}

function setIcon(button, added) {
    const icon = button.querySelector('.action-button-icon');

    if (icon) {
        icon.innerHTML = added ? heartFilledIcon : heartIcon;
    }

    // Communicate saved state to assistive tech (the button is a toggle).
    button.setAttribute('aria-pressed', added ? 'true' : 'false');

    if (button.dataset.labelAdd && button.dataset.labelRemove) {
        button.setAttribute('aria-label', added ? button.dataset.labelRemove : button.dataset.labelAdd);
    }

    // The visible word, where the button carries one: "Save" becomes "Saved",
    // matching the header heart, so the click has a result beyond the fill.
    const label = button.querySelector('[data-shortlist-label]');

    if (label && label.dataset.labelSave && label.dataset.labelSaved) {
        label.textContent = added ? label.dataset.labelSaved : label.dataset.labelSave;
    }
}

// What the shortlist page is actually showing, which is not always what was
// asked for: /properties/previews returns PUBLISHED properties only, so a house
// taken offline since the list was saved - or since the link was sent - renders
// as nothing. Counting the ids would then caption four cards "5 Houses".
//
// Falls back to the requested count before the grid has rendered, so the
// heading is never blank while the request is in flight.
function renderedCount(fallback) {
    const results = document.querySelector('[data-shortlist-results]');

    if (!results || !results.querySelector('.property-preview')) {
        return fallback;
    }

    return results.querySelectorAll('.property-preview').length;
}

function refreshTotals() {
    const count = favouriteCount();

    // Two different counts on a shared list, deliberately. The page heading
    // describes what is on screen (someone else's houses); the header heart
    // and its badge describe what THIS browser has saved, which is untouched
    // by looking at somebody's list and must not be overwritten by it.
    const shared = sharedFavourites();
    const onPage = renderedCount(shared ? shared.length : count);

    document.querySelectorAll('[data-shortlist-total]').forEach((element) => {
        element.innerHTML = onPage;
    });

    // Count badges (e.g. the header "Saved" heart) only show when something is
    // saved; the markup renders hidden server-side and JS reveals it here.
    document.querySelectorAll('[data-shortlist-badge]').forEach((element) => {
        element.classList.toggle('hidden', count === 0);
    });

    // Match the noun to the count: "1 House Shortlisted" / "2 Houses Shortlisted",
    // or the shared wording when the list belongs to someone else.
    document.querySelectorAll('[data-shortlist-noun]').forEach((element) => {
        const singular = shared ? element.dataset.sharedSingular : element.dataset.singular;
        const plural   = shared ? element.dataset.sharedPlural : element.dataset.plural;

        element.textContent = onPage === 1 ? singular : plural;
    });

    // Fill the header heart once something is saved, matching the card UI.
    document.querySelectorAll('[data-shortlist-heart]').forEach((element) => {
        element.innerHTML = count > 0 ? heartFilledIcon : heartIcon;
    });
}

// Hydrates every heart under `root` from the local store (cards render the empty
// state server-side; saved ones get filled here) and binds the toggle handler.
// Idempotent: the bound flag means re-running after AJAX re-renders only wires
// freshly injected hearts, never double-binds existing ones.
function hydrateAndBind(root = document) {
    root.querySelectorAll('[data-add-to-shortlist]').forEach((button) => {
        const propertyId = parseInt(button.getAttribute('data-add-to-shortlist'));

        setIcon(button, isFavourite(propertyId));

        if (button.dataset.favouriteBound) {
            return;
        }

        button.dataset.favouriteBound = '1';

        button.addEventListener('click', (event) => {
            event.preventDefault();

            const added = toggleFavourite(propertyId);

            // Reflect the new state on every heart for this property (a card and
            // the single-property header can both be on screen).
            document.querySelectorAll(`[data-add-to-shortlist="${propertyId}"]`).forEach((target) => {
                setIcon(target, added);
            });

            // Sync first: refreshTotals() now counts the cards on the page,
            // so removing one has to happen before the count is taken.
            syncShortlistPage();
            refreshTotals();
        });
    });
}

// Keep the shortlist page in sync when a card is un-saved there: drop the card,
// and swap to the empty state once none are left. No-op off the shortlist page.
function syncShortlistPage() {
    const results = document.querySelector('[data-shortlist-results]');

    if (!results) {
        return;
    }

    // On a shared list the hearts still work - the point is to save someone
    // else's houses to your own shortlist - but un-hearting one must not
    // delete it from the page. It is not your list to edit, and a card
    // vanishing from under a visitor reads as a bug.
    if (sharedFavourites()) {
        return;
    }

    results.querySelectorAll('[data-add-to-shortlist]').forEach((button) => {
        if (!isFavourite(button.getAttribute('data-add-to-shortlist'))) {
            button.closest('.property-preview')?.remove();
        }
    });

    if (!results.querySelector('.property-preview')) {
        document.querySelector('[data-shortlist-populated]')?.classList.add('hidden');
        document.querySelector('[data-shortlist-empty]')?.classList.remove('hidden');
    }
}

// Shortlist page: fetch preview cards for the browser's saved IDs and inject
// them, or reveal the empty state. Gated on the results container so this is a
// no-op everywhere else (no dedicated Vite entry needed).
function renderShortlistPage() {
    const results = document.querySelector('[data-shortlist-results]');

    if (!results) {
        return;
    }

    const populated = document.querySelector('[data-shortlist-populated]');
    const empty = document.querySelector('[data-shortlist-empty]');
    const favourites = sharedFavourites() || getFavourites();

    const showEmpty = () => empty && empty.classList.remove('hidden');

    if (!favourites.length) {
        showEmpty();

        return;
    }

    axios.get('/wp-json/lh/v1/properties/previews', {
        params: { ids: favourites.join(',') },
    })
        .then((response) => {
            const html = (response?.data?.html || '').trim();

            if (!html) {
                showEmpty();

                return;
            }

            results.innerHTML = html;

            if (populated) {
                populated.classList.remove('hidden');
            }

            refreshTotals();
            hydrateAndBind(results);
        })
        .catch(() => {
            showEmpty();
        });
}

// Re-wire hearts after an AJAX re-render injects new property cards (search
// filters, map info-windows, the owner dashboard). Idempotent via hydrateAndBind.
export function hydrateFavourites() {
    hydrateAndBind();
    refreshTotals();
}

export function initFavourites() {
    hydrateAndBind();
    refreshTotals();
    renderShortlistPage();
}
